import { callCenterStatusEnum, callDirection, callType, dialingScenes } from '@/enums/callCenter'
import {
  callCenterCheck,
  callCenterLinkInfo,
  queryPlaintextPhoneNum,
  queryPlaintextDataApi,
  releaseGlobalDialLock,
  sipQuickDialRelateCdr
} from '@/apis/callCenter/callCenter'

import { useAccountStore } from '@/stores/modules/account'
import { useCallCenter } from '@/stores/modules/callCenter'
import type { CallUserInfoVO, CallUserInfo } from '@/types/callCenter/callCenter'
import { isNullOrUndefOrEmpty, isUndef } from '@/utils/is'
import { computed, ref } from 'vue'
import { useDaco } from './daco'
import { useJsSIP } from './jssip'
import { useResource } from '../useResource'
import { rcProcessRelateNewCustomer, roundCallRelateCustomer } from '@/apis/callCenter/turnCall'
import { router } from '@/router'
import { getCustomerRedirectFlagApi, getCustomerDetailByIdApi } from '@/apis/customerManage'
import { cloneDeep } from '@/utils/tools'
/**
 * 呼叫相关方法
 */

const useDacoHooks = ref()
const useJsSIPHooks = ref()
const callCenter = ref()

export function useCall() {
  const accountStore = useAccountStore()
  const userInfo = computed(() => {
    return accountStore.userInfo
  })
  callCenter.value = useCallCenter()

  const loading = ref()
  /**
   * @returns 可拨打前置可行性校验
   */
  function callPreCheck() {
    let status = true
    const staffDefaultCallType = callCenter.value.staffDefaultCallType
    if (callCenter.value?.callCenterConfig?.seatExpired) {
      ElMessage.warning('坐席已过期，请续费')
      status = false
      return
    }
    if (callCenter.value.callCenterStatus === callCenterStatusEnum.STOP) {
      // 已停止
      const msg =
        staffDefaultCallType === 0 || staffDefaultCallType === 3
          ? '软电话启动失败，请设置默认呼叫方式'
          : staffDefaultCallType === 1
            ? '话机未初始化'
            : '服务连接失败'
      ElMessage.warning(msg)
      status = false
      return
    }
    if (callCenter.value.callCenterStatus === callCenterStatusEnum.CALLING) {
      ElMessage.warning('当前呼叫中，请稍后再拨')
      status = false
      return
    }
    if (staffDefaultCallType === callType.daco) {
      if (isNullOrUndefOrEmpty(callCenter.value.iccid)) {
        ElMessage.error('请检查sim卡')
        return false
      }
      if (!callCenter.value.usbStatus) {
        ElMessageBox.confirm('设备已断开，请检查话机连接线', '提示', {
          confirmButtonText: '确定',
          showCancelButton: false,
          type: 'warning'
        }).then(() => {})
        return false
      }
    }
    return status
  }

  /**
   * 拨打预检
   * @param calledPhone 被叫号码
   * @param objectId 对应id
   * @param bizType 1-客户 3-客户联系人
   */
  function callCheck(calledPhone: string, objectId?: string, bizType?: number) {
    console.log(callCenter, 'callCentercallCenter')
    useDacoHooks.value = useDaco()
    useJsSIPHooks.value = useJsSIP()
    loading.value = ElLoading.service({
      lock: true
    })
    return new Promise((resolve, reject) => {
      callCenterCheck({
        bizType: callCenter.value.dialingScenes === dialingScenes.singleCall ? bizType : '',
        staffId: userInfo.value.id,
        callType: callCenter.value.staffDefaultCallType,
        objectId: callCenter.value.dialingScenes === dialingScenes.singleCall ? objectId : '',
        imei: callCenter.value.staffDefaultCallType === callType.daco ? callCenter.value.imei : '',
        iccid:
          callCenter.value.staffDefaultCallType === callType.daco ? callCenter.value.iccid : '',
        callerPhone:
          callCenter.value.staffDefaultCallType === callType.daco
            ? callCenter.value.callerPhone
            : '',
        lineId:
          callCenter.value.staffDefaultCallType === callType.sip
            ? callCenter.value?.lineInfo?.lineId
            : '',
        calledPhone: calledPhone,
        feeFlag:
          callCenter.value.staffDefaultCallType === callType.sip
            ? callCenter.value?.lineInfo?.feeFlag
            : '',
        allUsedFlag:
          callCenter.value.staffDefaultCallType === callType.sip
            ? callCenter.value?.lineInfo?.allUsedFlag
            : '',
        quickDial: callCenter.value.dialingScenes === dialingScenes.quickCall
      })
        .then(res => {
          callCenter.value.dialingScenes === dialingScenes.quickCall
            ? getCallCenterLinkInfo(res, calledPhone)
            : singleCall(res, objectId)
          resolve(res)
        })
        .catch(err => {
          console.log(err, '拨打电话预检error')
          reject(err)
          loading.value.close()
          if (err.code === '103') {
            // store.dispatch('setIsRefreshTable', true)
            // 需要切卡
            useDacoHooks.value.changeSim()
          } else if (err.code === '108') {
            useDacoHooks.value.bindPhoneNumber()
          } else {
            releaseGlobalDialLock()
          }
        })
    })
  }
  // 单呼
  function singleCall(data: Recordable, objectId?: string) {
    const _data = data || {}
    // 存储被叫用户信息
    const userInfoVO = {
      title: {
        ..._data,
        unknow: false,
        customerId: _data.objectId,
        objectId: _data.bizType === 3 ? objectId : _data.objectId,
        dataFrom: 1
      },
      userList: [],
      gcId: _data.gcId || ''
    }
    callCenter.value.setCallUserInfo(userInfoVO)
    if (callCenter.value.staffDefaultCallType === callType.daco) {
      // 设置话机是否脱敏
      useDacoHooks.value.setHidNumber(_data?.deviceDesensitization || false)
      // sim卡号码已绑定,存储通话录音地址
      useDacoHooks.value.getCallNumber()
    }
    if (callCenter.value.staffDefaultCallType === callType.sip) {
      useJsSIPHooks.value.freeSwich(_data.plaintextPhone)
    }
    loading.value.close()
  }

  /**
   * 快速拨号获取用户关联信息
   * @callCheck 预检接口返回的数据
   */
  async function getCallCenterLinkInfo(callCheck: Recordable, calledPhone: string) {
    try {
      const res = await callCenterLinkInfo({
        callerPhone: callCheck.plaintextPhone
      })
      let userInfoVO: CallUserInfoVO = {}
      userInfoVO = {
        title: {
          unknow: true,
          linkName: '未知客户',
          realName: '',
          phoneCheckResult: callCheck.phoneCheckResult || '',
          desensitizePhoneNum: callCheck.desensitizePhoneNum || calledPhone,
          plaintextPhone: callCheck.plaintextPhone || calledPhone,
          dataFrom: 1
        },
        userList: [],
        gcId: callCheck.gcId || ''
      }
      // 一个客户，判断归属人
      if (res && res.length === 1) {
        userInfoVO!.title!.unknow = false
        userInfoVO!.title!.linkName = res[0].linkName
        userInfoVO!.title!.realName = res[0].realName
        userInfoVO!.title! = {
          ...userInfoVO!.title!,
          ...res[0]
        }
      } else if (res && res.length > 1) {
        // 一个号码绑定多个客户时，如果含有归属人是自己的时候，过滤掉归属人不是自己的和没有归属人的数据，如果过滤掉数据后只有一条，就直接关联客户，跳转到客户详情
        // 判断是否有归属人是自己的数据
        const isHasOwn = res?.some((item: CallUserInfo) => item.followUserId === userInfo.value?.id)
        let filterUserInfoList = cloneDeep(res)
        console.log(isHasOwn, 'isHasOwn')
        if (isHasOwn) {
          // 过滤掉归属人不是自己的和没有归属人的数据
          filterUserInfoList = res?.filter(
            (item: CallUserInfo) => item.followUserId === userInfo.value?.id
          )
        }
        console.log(filterUserInfoList, 'filterUserInfoList')
        if (filterUserInfoList?.length === 1) {
          userInfoVO!.title!.unknow = false
          userInfoVO!.title!.linkName = filterUserInfoList[0].linkName
          userInfoVO!.title!.realName = filterUserInfoList[0].realName
          userInfoVO!.title! = {
            ...userInfoVO!.title!,
            ...filterUserInfoList[0]
          }
        } else {
          userInfoVO.userList = filterUserInfoList
          userInfoVO!.title!.unknow = false
        }
      }
      callCenter.value.setCallUserInfo(userInfoVO)
      if (callCenter.value.staffDefaultCallType === callType.daco) {
        // 设置话机是否脱敏
        useDacoHooks.value.setHidNumber(callCheck.deviceDesensitization)
        // sim卡号码已绑定,存储通话录音地址
        useDacoHooks.value.getCallNumber()
      }
      if (callCenter.value.staffDefaultCallType === callType.sip) {
        useJsSIPHooks.value.freeSwich(callCheck.plaintextPhone)
      }
      loading.value.close()
      return userInfoVO
    } catch (error) {
      loading.value.close()
      return false
    }
  }

  /**
   * 拨打后处理
   */
  function callAfterFunc() {}

  /**
   * 获取号码明文
   * @param bizCode 10-通话记录业务, 20-轮呼已联系, 30-轮呼待联系，40-普通客户，50-企业客户，60-普通客户联系人，70-企业客户联系人， 80-群呼客户
   * @param dataId 通话记录ID，轮呼联系人ID
   */
  async function queryPlaintextPhoneNumber(data: Recordable) {
    return new Promise((resolve, reject) => {
      queryPlaintextPhoneNum({
        ...data
      })
        .then(res => {
          resolve(res)
        })
        .catch(error => {
          reject(error)
        })
    })
  }
  /**
   * 获取明文信息-暂时会返回电话和客户名称
   * @param bizCode 10-通话记录业务, 20-轮呼已联系, 30-轮呼待联系，40-普通客户，60-普通客户联系人， 80-群呼客户， 90 快速呼叫
   * @param dataId 通话记录ID，轮呼联系人ID   群呼助手传群呼助手列表的id，即群呼联系人id
   */
  async function queryPlaintextInfo(data: Recordable) {
    return new Promise((resolve, reject) => {
      queryPlaintextDataApi({
        ...data
      })
        .then(res => {
          resolve(res)
        })
        .catch(error => {
          reject(error)
        })
    })
  }
  /**
   * 判断联系人名称和被叫号码是否加密，其中一个加密，就调取后端接口进行解密
   * @param name 联系人名称被叫号码
   */
  function isEncryption({ name, phone }: { name: string; phone: string }) {
    return name?.includes('*') || phone?.includes('*')
  }

  /**
   * 跳转详情页
   * @param dataFrom: 数据来源 1:客户表 2:回收站 3:公海
   * @param orderType：数据类型 0-客户 1-线索
   * @param orderId 客户ID
   * @param realName: 客户姓名
   * @param jumpCallBack: 特殊处理回调，如果不是按照路由跳转，就传它，可在客户详情-关联客户跳转中查看使用
   */
  function jumpToDetail(orderId: string, realName: string, jumpCallBack?: Fn) {
    getCustomerRedirectFlagApi({
      id: orderId
    }).then(res => {
      // console.log(res)
      jumpToCustomerDetail({ ...res, orderId, realName }, jumpCallBack)
    })
  }

  function jumpToCustomerDetail(params: any, jumpCallBack?: Fn) {
    console.log(params)

    const { getMenuInfo } = useResource()

    const { movedToGh, toRecycle, orderId, realName, orderType } = params
    const menuCodeObj: Recordable = {
      0: {
        HighSeaManage: {
          menuPath: 'highSeaManage',
          path: '/highSeaDetail'
        },
        RecycleBin: {
          menuPath: 'recycleBin',
          path: '/recycleBinDetail'
        },
        CustomerManage: {
          menuPath: 'customerManage',
          path: '/customerDetail'
        }
      },
      1: {
        HighSeaManage: {
          menuPath: 'clueHighSeasManage',
          path: '/clueHighSeasDetail'
        },
        RecycleBin: {
          menuPath: 'clueRecycleManage',
          path: '/clueRecycleDetail'
        },
        CustomerManage: {
          menuPath: 'clueManage',
          path: '/clueDetail'
        }
      }
    }

    let menuObj: Recordable = {}

    if (movedToGh) {
      // 公海
      menuObj = menuCodeObj[orderType].HighSeaManage
    } else if (toRecycle) {
      // 回收
      menuObj = menuCodeObj[orderType].RecycleBin
    } else {
      // 客户
      menuObj = menuCodeObj[orderType].CustomerManage
    }
    console.log(menuObj)
    getMenuInfo('CRM', menuObj.menuPath, false).then(res => {
      if (!isNullOrUndefOrEmpty(res.tableCode)) {
        // 线索回收先屏蔽
        if (menuObj.menuPath === 'clueRecycleManage') {
          ElMessage.error('暂不支持操作！')
          return false
        }

        const tableCode = res.tableCode
        // 这里只是处理线索客户和客户管理
        if (['clueManage', 'customerManage'].includes(menuObj.menuPath)) {
          getCustomerDetailByIdApi(
            { shopOrderId: orderId, tableCode: tableCode },
            { MenuId: res.id }
          )
            .then(res => {
              if (res && res.id) {
                if (jumpCallBack) {
                  jumpCallBack(
                    router.resolve(
                      `${menuObj.path}?id=${orderId}&tableCode=${tableCode}&realName=${realName}`
                    )
                  )
                } else {
                  if (
                    router.currentRoute.value.path === menuObj.path &&
                    router.currentRoute.value.query.id === orderId
                  ) {
                    return false
                  }
                  router.push(
                    `${menuObj.path}?id=${orderId}&tableCode=${tableCode}&realName=${realName}`
                  )
                }
              } else {
                ElMessage.error('暂无权限，请联系管理员开通权限')
              }
            })
            .catch(() => {
              ElMessage.error('暂无权限，请联系管理员开通权限')
            })
        } else {
          if (jumpCallBack) {
            jumpCallBack(
              router.resolve(
                `${menuObj.path}?id=${orderId}&tableCode=${tableCode}&realName=${realName}`
              )
            )
          } else {
            if (
              router.currentRoute.value.path === menuObj.path &&
              router.currentRoute.value.query.id === orderId
            ) {
              return false
            }
            router.push(`${menuObj.path}?id=${orderId}&tableCode=${tableCode}&realName=${realName}`)
          }
        }
      } else {
        ElMessage.error('暂无权限，请联系管理员开通权限')
      }
    })
  }

  /**
   * 轮呼或者轮呼过程中来电
   */
  async function roundCallRelate(customerId: string, roundCustomerId: string) {
    const loading = ElLoading.service()
    if (callCenter.value.staffDefaultCallType === callType.sip) {
      const uuid = callCenter.value.jssipCallId
      roundCallRelateCustomer({
        relateShopOrderId: customerId,
        relateLinkId: customerId,
        id: roundCustomerId
      }).then(() => {
        sipQuickDialRelateCdr({
          gcId: uuid,
          bizType: 1,
          objectId: roundCustomerId,
          dataType: 1,
          dialScene: 1
        })
        loading.close()
      })
    } else {
      loading.close()
      roundCallRelateCustomer({
        relateShopOrderId: customerId,
        relateLinkId: customerId,
        id: roundCustomerId,
        toBeContacted: false
      }).then(() => {
        if (isUndef(useDacoHooks.value)) {
          useDacoHooks.value = useDaco()
          useDacoHooks.value.setRoundCallrecordUrl('save')
        } else {
          useDacoHooks.value.setRoundCallrecordUrl('save')
        }
      })
    }
  }

  /**
   * 快速呼叫或者来电
   */
  async function quickCallRelate(customerId: string) {
    const loading = ElLoading.service()
    if (callCenter.value.staffDefaultCallType === callType.sip) {
      // const uuid = this.$store.getters.lineInfo.serverType === 2 ? this.$store.getters.sipCallId : setRules(userInfos.titleInfo.plaintextPhone, userInfos.gcId, this.$store)
      const uuid = callCenter.value.jssipCallId
      sipQuickDialRelateCdr({
        gcId: uuid,
        objectId: customerId,
        dataType: 1,
        bizType: 1
      })
      loading.close()
    } else {
      loading.close()
      useDacoHooks.value.setCallrecordUrl('save')
    }
  }

  /**
   * PC唤起APP轮呼
   */
  async function relateTurnCallApp(customerId: string, userInfoVO?: CallUserInfoVO) {
    // 如果APP是SIP呼叫，PC端进行关联
    if (userInfoVO?.title?.callType === 2) {
      sipQuickDialRelateCdr({
        gcId: userInfoVO.gcId,
        bizType: 1,
        objectId: userInfoVO?.title?.roundCustomerId,
        dataType: 1,
        dialScene: 1
      })
    }
    if (userInfoVO?.title?.callType === 3) {
      // 如果是本机呼叫
      rcProcessRelateNewCustomer({
        localDialId: userInfoVO?.localDialId,
        bizType: 1,
        objectId: customerId,
        linkId: customerId,
        dataType: 1,
        dialScene: 1
      })
    }
    Promise.all([
      // APP轮呼新建关联客户
      roundCallRelateCustomer({
        linkType: 1,
        relateShopOrderId: customerId,
        relateLinkId: customerId,
        id: userInfoVO?.title?.roundCustomerId,
        toBeContacted: false
      })
    ])
  }

  /**
   * 客户详情页写跟进更新用户信息
   */
  async function updateCallUserInfo(customerDetail: Recordable, userInfoVO: CallUserInfoVO) {
    // 呼叫中心-写跟进判断
    const userInfo = cloneDeep(userInfoVO)
    userInfo.title.linkName = customerDetail?.linkName
    userInfo.title.realName = customerDetail?.realName
    userInfo.title.plaintextPhone = customerDetail?.linkPhone
    userInfo.title.plaintextPhoneNum = customerDetail?.linkPhone
    userInfo.title.followUserId = customerDetail?.followInfoVo?.id
    userInfo.userList = []
    if (callCenter.value.dialingScenes === dialingScenes.groupCall) {
      callCenter.value.setGroupCallUserInfo(userInfo)
    } else {
      callCenter.value.callDirection === callDirection.in
        ? callCenter.value.setCallUserInfoIn(userInfo)
        : callCenter.value.setCallUserInfo(userInfo)
    }
    console.log(userInfo, '更新客户信息')
  }

  return {
    callCheck,
    getCallCenterLinkInfo,
    callAfterFunc,
    queryPlaintextPhoneNumber,
    callPreCheck,
    jumpToCustomerDetail,
    jumpToDetail,
    roundCallRelate,
    quickCallRelate,
    relateTurnCallApp,
    updateCallUserInfo,
    queryPlaintextInfo,
    isEncryption
  }
}
